import React from "react";

import { Avatar, Group, LoadingOverlay, Stack, Table, Text, Tooltip } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import _ from "lodash";

import { BENCHMARK_DATASET } from "constants/enums";
import { descriptions } from "constants/levelData";
import { useIsLoadingSearchData, useSearchRouteInfo } from "hooks/routing";
import { SaveBenchmarkButton } from "lib/benchmarking/components/SaveBenchmarkButton";
import { FamilyLevelData, QueryArgs } from "lib/frontend/benchmarking/types";
import { useIsSmallerThan } from "ui/theme/helpers";
import {
  QUERY_ARGS_THAT_ARE_NOT_FILTERS,
  formatNum,
  getLevelSortKey,
  getSizeTrust,
  levelLabel,
} from "utils/utils";

import { SampleSizeBadge } from "./SampleSizeBadge";
import { useSelectedPercentile } from "./SelectedPercentileProvider";

interface LevelComparisonTableProps {
  data: FamilyLevelData[];
}

function areFiltersApplied(query: QueryArgs): boolean {
  const relevantQuery = _.omit(query, QUERY_ARGS_THAT_ARE_NOT_FILTERS);
  // @ts-expect-error TS2769
  return _.some(relevantQuery, (v) => v && v.length > 0);
}

export function LevelComparisonTable({ data }: LevelComparisonTableProps): JSX.Element {
  const { selectedPercentile } = useSelectedPercentile();
  const isSmall = useIsSmallerThan("sm");
  const searchInfo = useSearchRouteInfo();

  const lowPercentileKey = `rangeLowPercentile${selectedPercentile}`;
  const highPercentileKey = `rangeHighPercentile${selectedPercentile}`;

  const rowData = _(data)
    .filter((row) => row.numUniqueRanges > 0)
    .filter((row) => !_.isNil(descriptions[row.level as keyof typeof descriptions]))
    .sortBy((r) => getLevelSortKey(r.level))
    .valueOf();

  const { query, replaceQuery } = useSearchRouteInfo();
  const loading = useIsLoadingSearchData();

  const currentLevel = query.level?.[0];

  const filtersApplied = areFiltersApplied(query);

  return (
    <Table highlightOnHover horizontalSpacing={16} sx={{ width: "100%" }}>
      <LoadingOverlay
        visible={loading}
        overlayProps={{
          blur: 1,
        }}
        zIndex={80}
        loaderProps={{
          sx: {
            position: "absolute",
            top: 200,
          },
        }}
      />
      <Table.Thead
        style={{
          backgroundColor: "var(--mantine-color-gray-1)",
        }}
      >
        <Table.Tr>
          {/* <th style={{ width: 100, fontWeight: 100, paddingLeft: 16 }}>Level</th> */}
          <Table.Th style={{ width: 400, fontWeight: 100, paddingLeft: 16, minWidth: 200 }}>
            Level
          </Table.Th>
          <Table.Th style={{ width: 150, fontWeight: 100, textAlign: "center" }}>
            Sample Size
          </Table.Th>
          <Table.Th></Table.Th>
          <Table.Th style={{ fontWeight: 100 }}>Low</Table.Th>
          <Table.Th style={{ fontWeight: 100 }}>Mid</Table.Th>
          <Table.Th style={{ fontWeight: 100 }}>High</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {_.map(rowData, (row) => {
          const lowPercentile = _.get(row, lowPercentileKey);
          const highPercentile = _.get(row, highPercentileKey);

          const low = lowPercentile ? formatNum(lowPercentile, false) : "N/A";
          const mid = highPercentile
            ? formatNum(_.mean([lowPercentile, highPercentile]), false)
            : "N/A";
          const high = highPercentile ? formatNum(highPercentile, false) : "N/A";

          const isCurrentlySelectedLevel = row.level === currentLevel;

          const descriptionObj: { title: string; description: string; YoE?: string } | undefined =
            descriptions[row.level as keyof typeof descriptions];

          const insufficientSampleSize = getSizeTrust(row.numUniqueRanges) === 0;

          return (
            <Table.Tr
              key={row.level}
              style={{
                cursor: "pointer",
                backgroundColor: isCurrentlySelectedLevel
                  ? "var(--mantine-color-brand-1)"
                  : undefined,
              }}
              onClick={() => {
                replaceQuery({
                  level: [row.level],
                });
              }}
            >
              <Table.Td style={{ paddingLeft: 16 }}>
                <Group wrap="nowrap" gap={16}>
                  {!isSmall && (
                    <Avatar
                      radius="50%"
                      variant="white"
                      color={isCurrentlySelectedLevel ? "brand" : "gray"}
                      sx={{
                        border: `1px solid ${
                          isCurrentlySelectedLevel
                            ? "var(--mantine-color-brand-5)"
                            : "var(--mantine-color-gray-4)"
                        }`,
                      }}
                    >
                      {row.level}
                    </Avatar>
                  )}

                  {descriptionObj && (
                    <Stack gap={0}>
                      <Group align="flex-end">
                        <Text fz={14} fw={500}>
                          {isSmall ? levelLabel(row.level) : descriptionObj.title}
                        </Text>
                        <Text fz={12} fw={400} c="gray.6">
                          {descriptionObj.YoE}
                        </Text>
                      </Group>
                      <Text fz={12} fw={400} c="gray.6" mih={18 * 2} sx={{ lineHeight: "18px" }}>
                        {descriptionObj.description}
                      </Text>
                    </Stack>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>
                <Stack>
                  <Group w="100%" align="center">
                    <SampleSizeBadge sampleSize={row.numUniqueRanges} sx={{ flex: 1 }} />
                    {filtersApplied && insufficientSampleSize && (
                      <Tooltip label={"Consider removing filters to increase data sample size"}>
                        <IconInfoCircle size={16} color="var(--mantine-color-gray-5)" />
                      </Tooltip>
                    )}
                  </Group>
                  <Text ta="center" c="gray" fz="xs">
                    {row.numUniqueRanges.toLocaleString("en-US")}{" "}
                    {row.numUniqueRanges > 1 ? "Data Points" : "Data Point"}
                  </Text>
                </Stack>
              </Table.Td>
              <Table.Td onClick={(e) => e.stopPropagation()}>
                {row.numUniqueRanges > 0 && (
                  <SaveBenchmarkButton
                    dataset={BENCHMARK_DATASET.comprehensive}
                    jobTitle={(() => {
                      // Use jobFamily - level format for consistency
                      const jobFamily =
                        searchInfo.query.jobFamily?.[0] || searchInfo.query.jobFamily;
                      if (jobFamily) {
                        return `${jobFamily} - ${row.level}`;
                      }
                      // Fallback to descriptive title if no jobFamily
                      return `${descriptionObj?.title || levelLabel(row.level)}`;
                    })()}
                    location={(() => {
                      // For Comprehensive, format location array as a string
                      const loc = searchInfo.query.location;
                      if (Array.isArray(loc) && loc.length > 0) {
                        return loc.join(", ");
                      } else if (typeof loc === "string") {
                        return loc;
                      }
                      return undefined;
                    })()}
                    searchParams={(() => {
                      // Filter out empty/undefined values from searchInfo.query
                      const filteredParams: Record<string, any> = {};
                      Object.entries(searchInfo.query).forEach(([key, value]) => {
                        // Only include parameters that have actual values
                        if (
                          value != null &&
                          value !== "" &&
                          (!Array.isArray(value) || value.length > 0)
                        ) {
                          filteredParams[key] = value;
                        }
                      });
                      return {
                        ...filteredParams,
                        // Override the level to be specific to this row
                        level: row.level,
                      };
                    })()}
                    benchmarkData={{
                      percentiles: {
                        25: lowPercentile * 100,
                        50: _.mean([lowPercentile, highPercentile]) * 100,
                        75: highPercentile * 100,
                      },
                      dataPoints: row.numUniqueRanges,
                      raw: {
                        level: row.level,
                      },
                    }}
                  />
                )}
              </Table.Td>
              <Table.Td>
                <Text fz={14} fw={isCurrentlySelectedLevel ? 800 : 400}>
                  {low}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text fz={14} fw={isCurrentlySelectedLevel ? 800 : 400}>
                  {mid}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text fz={14} fw={isCurrentlySelectedLevel ? 800 : 400}>
                  {high}
                </Text>
              </Table.Td>
              {/* </Link> */}
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}
